import React, {
  useMemo, useRef, useState,
} from 'react';
import { format, parse } from 'date-fns';
import { useSelector } from 'react-redux';
import ReactECharts from 'components/ThemedECharts';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import Icon from 'components/DesignSystem/Icon';
import DoubleSlider from 'components/DesignSystem/Slider/DoubleSlider';
import CentralizedSpinner from 'components/CentralizedSpinner';
import About from 'components/Meteorology/Agro/CultureIndex/Location/About';
import useCharts from 'hooks/Meteorology/Agro/CultureIndex/Location/useCharts';
import useAutoLoad from 'hooks/Meteorology/Agro/CultureIndex/Location/useAutoLoad';
import useLocations from 'hooks/Meteorology/Agro/useLocations';
import useDownloadDivImage from 'hooks/useDownloadDivImage';
import {
  selectMeteorologyAgroCultureIndexLocationCulture,
  selectMeteorologyAgroCultureIndexLocationStage,
  selectMeteorologyAgroCultureIndexLocationForecastDate,
  selectMeteorologyAgroCultureIndexLocationPeriodStart,
  selectMeteorologyAgroCultureIndexLocationPeriodEnd,
  selectMeteorologyAgroCultureIndexLocationLocationId,
} from 'redux/reducers/meteorologyAgroCultureIndexLocationReducer';
import { Charts } from 'types/Meteorology/Agro/CultureIndex/Location/getCharts';
import { cssVar } from 'constants/theme';

import ControlPanelSheet from './ControlPanelSheet';
import EmptyState from './EmptyState';
import FullscreenChart from './FullscreenChart';
import buildMobileChartOption from './chartOption';

import './index.scss';

const parseDate = (value?: string) => (value ? parse(value, 'yyyy-MM-dd', new Date()) : null);
const formatDate = (value?: string) => {
  const date = parseDate(value);
  return date ? format(date, 'dd/MM/yy') : undefined;
};

const chartTitle = (option: Charts, index: number) => (
  (option.title as { text?: string } | undefined)?.text || `Gráfico ${index + 1}`
);

export default function LocationMobile() {
  // Carrega automaticamente quando vem do clique num pin do mapa de Área.
  useAutoLoad();

  const { data, isFetching } = useCharts();
  const { locations } = useLocations();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(100);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const downloadImage = useDownloadDivImage(chartContainerRef);

  const culture = useSelector(selectMeteorologyAgroCultureIndexLocationCulture);
  const stage = useSelector(selectMeteorologyAgroCultureIndexLocationStage);
  const forecastDate = useSelector(selectMeteorologyAgroCultureIndexLocationForecastDate);
  const periodStart = useSelector(selectMeteorologyAgroCultureIndexLocationPeriodStart);
  const periodEnd = useSelector(selectMeteorologyAgroCultureIndexLocationPeriodEnd);
  const locationId = useSelector(selectMeteorologyAgroCultureIndexLocationLocationId);

  const setZoom = ([start, end]: number[]) => {
    setZoomStart(start);
    setZoomEnd(end);
  };

  const locationName = useMemo(
    () => locations?.find((l) => l.id === locationId)?.name,
    [locations, locationId],
  );

  const chips = useMemo(() => {
    const result: string[] = [];
    if (culture?.label) result.push(culture.label);
    if (stage?.label) result.push(stage.label);
    if (forecastDate) result.push(`Previsão: ${formatDate(forecastDate)}`);
    if (periodStart && periodEnd) {
      result.push(`${formatDate(periodStart)} – ${formatDate(periodEnd)}`);
    }
    return result;
  }, [culture, stage, forecastDate, periodStart, periodEnd]);

  const periodStartDate = parseDate(periodStart);
  const periodEndDate = parseDate(periodEnd);

  const zoomLabel = useMemo(() => {
    if (!periodStartDate || !periodEndDate) return undefined;
    const totalMs = periodEndDate.getTime() - periodStartDate.getTime();
    const from = new Date(periodStartDate.getTime() + (totalMs * zoomStart) / 100);
    const to = new Date(periodStartDate.getTime() + (totalMs * zoomEnd) / 100);
    return `${format(from, 'dd/MM/yy')} – ${format(to, 'dd/MM/yy')}`;
  }, [periodStartDate, periodEndDate, zoomStart, zoomEnd]);

  const charts = data as Charts[] | undefined;
  const hasQuery = charts !== undefined && charts.length > 0;

  const fullscreenOption = fullscreenIndex !== null ? charts?.[fullscreenIndex] : undefined;

  return (
    <div className="agro-ci-location-mobile">
      <QueryCard>
        <QueryCardHeader label="Consulta atual" title={locationName || 'Nenhum local selecionado'}>
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      {isFetching && <CentralizedSpinner />}

      {!isFetching && !hasQuery && (
        <EmptyState
          icon="filter"
          title="Nenhuma consulta ainda"
          text="Defina cultura, local e período no painel de controle para visualizar o índice."
          actionLabel="Abrir painel"
          onAction={() => setSheetOpen(true)}
        />
      )}

      {!isFetching && hasQuery && (
        <div className="agro-ci-location-mobile-panel">
          <div className="agro-ci-location-mobile-actions">
            <button
              type="button"
              aria-label="Baixar imagem"
              className="agro-ci-location-mobile-action"
              onClick={downloadImage('indice_por_cultura.png')}
            >
              <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Instruções"
              className="agro-ci-location-mobile-action agro-ci-location-mobile-action-info"
              onClick={() => setShowAbout(true)}
            >
              <Icon iconType="info" color={cssVar('text-on-accent')} size="small" />
            </button>
          </div>

          <div ref={chartContainerRef} className="agro-ci-location-mobile-chart-stack">
            {charts.map((option, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={chartTitle(option, index)}
                className="agro-ci-location-mobile-chart-card"
              >
                <div className="agro-ci-location-mobile-chart-card-header">
                  <span className="agro-ci-location-mobile-chart-card-title">
                    {chartTitle(option, index)}
                  </span>
                  <button
                    type="button"
                    aria-label="Tela cheia"
                    className="agro-ci-location-mobile-chart-card-fullscreen"
                    onClick={() => setFullscreenIndex(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                  </button>
                </div>
                <ReactECharts
                  className="agro-ci-location-mobile-chart"
                  option={buildMobileChartOption(option, zoomStart, zoomEnd)}
                  style={{ height: '18rem' }}
                />
              </div>
            ))}
          </div>

          <div className="agro-ci-location-mobile-zoom-bar">
            <div className="agro-ci-location-mobile-zoom-bar-row">
              <span className="agro-ci-location-mobile-zoom-bar-label">
                <Icon iconType="search" color={cssVar('text-strong')} size="small" />
                Zoom sincronizado
              </span>
              {zoomLabel && (
                <span className="agro-ci-location-mobile-zoom-bar-range">{zoomLabel}</span>
              )}
            </div>
            <DoubleSlider
              min={0}
              max={100}
              value={[zoomStart, zoomEnd]}
              setValue={setZoom}
            />
            {periodStartDate && periodEndDate && (
              <div className="agro-ci-location-mobile-zoom-bar-bounds">
                <span>{format(periodStartDate, 'dd/MM')}</span>
                <span>{format(periodEndDate, 'dd/MM')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <About show={showAbout} onHide={() => setShowAbout(false)} />
      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
      <FullscreenChart
        open={fullscreenOption !== undefined}
        dismiss={() => setFullscreenIndex(null)}
        title={fullscreenOption ? chartTitle(fullscreenOption, fullscreenIndex ?? 0) : undefined}
        option={fullscreenOption}
        zoomStart={zoomStart}
        zoomEnd={zoomEnd}
      />
    </div>
  );
}
